import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, IconButton, Tooltip, Typography} from '@mui/material';
import {useLocation} from 'react-router';
import ContextBar from './components/ContextBar';
import ThreadHistory from './components/ThreadHistory';
import {AIAssistantChatPanel} from './index';
import useAiWidgetStore from '../../stores/useAiWidgetStore';
import {useReportStore} from '../products/useReportStore';
import useChartAiContext from '../../hooks/useChartAiContext';
// MINSKY-16657: recent-thread strip + history drawer now read from the agentic ai-assistant
// endpoints (MINSKY-16656) instead of the legacy `chatApi.js`'s message-based endpoints, matching
// `AIAssistantChatPanel.jsx`'s controller cutover — see `ThreadHistory.jsx`'s header comment for
// the shape-compatibility note.
// MINSKY-16473 ticket 13: both now live on dash-api's own base URL — no more separate service URL.
import {listAgenticConversations} from './services/agenticRun/agenticRunApi';
import {resolveConversationForChart} from './utils/chartConversationHistory';
import {AI_ASSISTANT_CHAT_API_BASE} from './services/api/chatApi';
import SubjectIcon from '@mui/icons-material/Subject';
import NewConversationButton from './components/NewConversationButton';

/**
 * The AI tab content inside CommunicationHub.
 *
 * Header line: history | current thread title | new chat
 * Below: ContextBar + AIAssistantChatPanel.
 */
const GlobalAiAssistantPanel = ({isActive = true, startNewConversationRef}) => {
    const location = useLocation();
    const aiContext = useAiWidgetStore((s) => s.aiContext);
    const threadId = useAiWidgetStore((s) => s.threadId);
    const setThreadId = useAiWidgetStore((s) => s.setThreadId);
    const setChartThreadId = useAiWidgetStore((s) => s.setChartThreadId);
    const clearThreadId = useAiWidgetStore((s) => s.clearThreadId);
    const chartContext = useChartAiContext();

    const [showHistory, setShowHistory] = useState(false);
    const [historyScope, setHistoryScope] = useState('chart');
    const previousChartIdRef = useRef(undefined);

    const [recentThreads, setRecentThreads] = useState([]);

    // ref wired to AIAssistantChatPanel's startNewConversation callback
    const internalStartNewRef = useRef(null);

    const editorMatch = useMemo(() => {
        const m = location.pathname.match(/^\/products\/([^/]+)/);
        return m ? m[1] : null;
    }, [location.pathname]);

    const editorReportId = useReportStore((s) => s.reportId);
    const editorTitle = useReportStore((s) => s.title || '');
    const editorProductType = useReportStore((s) => s.productType?.value || 'report');

    const suggestedContext = useMemo(() => {
        // Chart routes use chart grounding — do not auto-suggest report documents.
        if (chartContext?.chartId) return null;
        if (!editorMatch || !editorReportId) return null;
        return {
            documentId: editorReportId,
            documentTitle: editorTitle || editorReportId,
            documentType: editorProductType,
        };
    }, [chartContext?.chartId, editorMatch, editorReportId, editorTitle, editorProductType]);

    // Derive flat values from the multi-document context (ignored on chart routes).
    const documents = chartContext?.chartId ? [] : (aiContext?.documents || []);
    const documentType = aiContext?.documentType || 'report';
    const documentIds = useMemo(() => documents.map((d) => d.documentId), [documents]);
    // Primary doc (first) for backwards-compat single-doc props
    const documentId = documentIds[0] || null;
    const documentTitle = useMemo(
        () => documents.map((d) => d.documentTitle).filter(Boolean).join(', '),
        [documents],
    );

    const hasContext = documents.length > 0;
    const emptyStatePreset = chartContext?.chartId
        ? 'chart'
        : (hasContext ? documentType : 'global');
    const emptyStateDocumentTitle = chartContext?.chartId
        ? (chartContext.title || 'Chart')
        : (documentTitle || 'Global');

    const refreshRecent = useCallback(() => {
        listAgenticConversations(AI_ASSISTANT_CHAT_API_BASE)
            .then((data) => setRecentThreads(Array.isArray(data) ? data : []))
            .catch((err) => console.error('[GlobalAiAssistantPanel] failed to load recent threads', err));
    }, []);

    const rememberThreadForChart = useCallback((chartId, id) => {
        if (chartId && id) {
            setChartThreadId(chartId, id);
        }
    }, [setChartThreadId]);

    const handleConversationChange = useCallback((id) => {
        setThreadId(id, chartContext?.chartId);
    }, [setThreadId, chartContext?.chartId]);

    // Load recent threads on mount
    useEffect(() => {
        refreshRecent();
    }, [refreshRecent]);

    // Refresh strip when returning from full history view (deletions may have occurred)
    useEffect(() => {
        if (!showHistory) refreshRecent();
    }, [showHistory, refreshRecent]);

    const resetAssistantForChartChange = useCallback(() => {
        clearThreadId();
        internalStartNewRef.current?.();
    }, [clearThreadId]);

    const handleNewThread = useCallback(() => {
        resetAssistantForChartChange();
        setShowHistory(false);
        refreshRecent();
    }, [resetAssistantForChartChange, refreshRecent]);

    // Restore the last conversation used on this chart (refresh, leave/return, or switch charts).
    useEffect(() => {
        const chartId = chartContext?.chartId ?? null;
        const previousChartId = previousChartIdRef.current;
        const leftChart = Boolean(previousChartId && previousChartId !== chartId);

        if (leftChart) {
            const leavingThreadId = useAiWidgetStore.getState().threadId;
            if (leavingThreadId) {
                rememberThreadForChart(previousChartId, leavingThreadId);
            }
        }

        previousChartIdRef.current = chartId;

        if (!chartId) {
            if (leftChart) {
                resetAssistantForChartChange();
            }
            return undefined;
        }

        let cancelled = false;
        listAgenticConversations(AI_ASSISTANT_CHAT_API_BASE)
            .then((data) => {
                if (cancelled) return;
                const threads = Array.isArray(data) ? data : [];
                setRecentThreads(threads);
                const currentThreadId = useAiWidgetStore.getState().threadId;
                const lastInteractedThreadId = useAiWidgetStore.getState().chartThreadIds?.[chartId] || null;
                const nextId = resolveConversationForChart({
                    threads,
                    chartId,
                    currentThreadId,
                    lastInteractedThreadId,
                });
                if (nextId === currentThreadId) {
                    if (nextId) {
                        rememberThreadForChart(chartId, nextId);
                    }
                    return;
                }
                if (nextId) {
                    setThreadId(nextId, chartId);
                    return;
                }
                if (currentThreadId) {
                    resetAssistantForChartChange();
                }
            })
            .catch((err) => console.error('[GlobalAiAssistantPanel] failed to restore chart conversation', err));

        return () => {
            cancelled = true;
        };
    }, [chartContext?.chartId, rememberThreadForChart, resetAssistantForChartChange, setThreadId]);

    // Wire to parent (CommunicationHub header "+ New AI chat" button)
    useEffect(() => {
        if (!startNewConversationRef) return undefined;
        startNewConversationRef.current = handleNewThread;
        return () => { startNewConversationRef.current = null; };
    }, [startNewConversationRef, handleNewThread]);

    const handleSelectThread = useCallback((id) => {
        setThreadId(id, chartContext?.chartId);
        setShowHistory(false);
    }, [setThreadId, chartContext?.chartId]);

    const handleThreadDeleted = useCallback((id) => {
        const chartId = chartContext?.chartId;
        if (chartId && useAiWidgetStore.getState().chartThreadIds?.[chartId] === id) {
            setChartThreadId(chartId, null);
        }
        if (id === threadId) {
            clearThreadId();
            internalStartNewRef.current?.();
        }
        refreshRecent();
    }, [chartContext?.chartId, threadId, setChartThreadId, clearThreadId, refreshRecent]);

    if (showHistory) {
        return (
            <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                <ThreadHistory
                    currentThreadId={threadId}
                    apiBaseUrl={AI_ASSISTANT_CHAT_API_BASE}
                    onSelect={handleSelectThread}
                    onNewThread={handleNewThread}
                    onClose={() => setShowHistory(false)}
                    onDeleted={handleThreadDeleted}
                    chartId={chartContext?.chartId || null}
                    historyScope={chartContext?.chartId ? historyScope : 'all'}
                    onHistoryScopeChange={setHistoryScope}
                />
            </Box>
        );
    }

    return (
        <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            {/* Header line: history | thread title | new */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1,
                flexShrink: 0,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}>
                <Tooltip title="Conversation history" placement="right" enterDelay={600} enterNextDelay={600}>
                    <IconButton size="small" onClick={() => setShowHistory(true)} aria-label="Conversation history">
                        <SubjectIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
                <Typography
                    variant="body2"
                    noWrap
                    sx={{flex: 1, mx: 0.5, color: threadId ? 'text.primary' : 'text.disabled'}}
                >
                    {recentThreads.find((t) => t.id === threadId)?.title ?? 'New conversation'}
                </Typography>
                <NewConversationButton onClick={handleNewThread}/>
            </Box>

            {/* Context bar */}
            <ContextBar suggestedContext={suggestedContext} chartContext={chartContext}/>

            <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                <AIAssistantChatPanel
                    conversationId={threadId}
                    documentId={documentId}
                    documentIds={documentIds}
                    documentType={documentType}
                    documentTitle={documentTitle}
                    chartContext={chartContext}
                    emptyStateDocumentTitle={emptyStateDocumentTitle}
                    emptyStatePreset={emptyStatePreset}
                    isActive={isActive}
                    showHeader={false}
                    onConversationChange={handleConversationChange}
                    startNewConversationRef={internalStartNewRef}
                />
            </Box>
        </Box>
    );
};

export default GlobalAiAssistantPanel;
